import { create } from "zustand";
import {
  availableQuantity,
  createSeededRandom,
  MATCH_START_SEQUENCE_MS,
  generateInventory,
  hashSeed,
  normalizeRecipeGrid,
  recipeMatches,
  ROUND_START_SEQUENCE_MS,
  ROUNDS_TO_WIN,
  selectRoundValueByFamily,
  shuffleSeeded,
  type Grid,
  type InventorySlot,
  type ItemId,
  type Recipe
} from "@craftoff/game-core";
import { DECOY_ITEMS, getMatchItemFamily, MATCH_RECIPES } from "@craftoff/recipe-data";
import { clientLog, clientWarn } from "./client-log";

export type GameStatus = "ready" | "countdown" | "active" | "round-result" | "match-result";
export type AiDifficulty = "easy" | "normal" | "hard" | "impossible";
export type OnlineAction =
  | { type: "MOVE_ITEM"; fromSlot: number; toSlot: number }
  | { type: "REMOVE_ITEM"; slot: number };

type RoundWinner = "player" | "rival" | "draw" | null;
type AiPlacement = { at: number; itemId: ItemId; slot: number };
type AiDifficultyProfile = {
  minDurationMs: number;
  maxDurationMs: number;
  jitterMin: number;
  jitterMax: number;
  hesitationChance: number;
};

const AI_DIFFICULTY_PROFILES: Record<AiDifficulty, AiDifficultyProfile> = {
  easy: { minDurationMs: 12_000, maxDurationMs: 18_000, jitterMin: 0.5, jitterMax: 1.6, hesitationChance: 0.22 },
  normal: { minDurationMs: 8_000, maxDurationMs: 12_000, jitterMin: 0.65, jitterMax: 1.45, hesitationChance: 0.15 },
  hard: { minDurationMs: 4_500, maxDurationMs: 7_000, jitterMin: 0.75, jitterMax: 1.3, hesitationChance: 0.09 },
  impossible: { minDurationMs: 2_000, maxDurationMs: 3_500, jitterMin: 0.88, jitterMax: 1.15, hesitationChance: 0.04 }
};

type GameState = {
  status: GameStatus;
  round: number;
  score: [number, number];
  recipe: Recipe;
  inventory: InventorySlot[];
  grid: Grid;
  rivalGrid: Grid;
  selectedItem: ItemId | null;
  draggingItem: ItemId | null;
  deadline: number | null;
  durationMs: number;
  aiDifficulty: AiDifficulty;
  rivalPlacementSchedule: AiPlacement[];
  rivalFinishAt: number | null;
  frozenUntil: number | null;
  inventoryScrambleRevision: number;
  winner: RoundWinner;
  soundEnabled: boolean;
  matchStartedAt: number | null;
  matchSeed: number;
  playKind: "ai" | "online" | "learn";
  onlineSend: ((action: OnlineAction) => void) | null;
  startRound: () => void;
  selectItem: (itemId: ItemId) => void;
  beginDrag: (itemId: ItemId) => void;
  endDrag: () => void;
  placeSelected: (slot: number, itemId?: ItemId) => void;
  clearSlot: (slot: number) => void;
  clearAllOfItem: (itemId: ItemId) => void;
  tick: (now: number) => void;
  nextRound: () => void;
  resetMatch: () => void;
  configureAiMatch: (difficulty: AiDifficulty) => void;
  configureLearnRecipe: (recipe: Recipe, sessionSeed: number, position: number) => void;
  configureOnlineMatch: (send: (action: OnlineAction) => void) => void;
  startOnlineCountdown: (round: number, recipe: Recipe, inventory: InventorySlot[], startsAt: number) => void;
  startOnlineRound: (round: number, recipe: Recipe, inventory: InventorySlot[], deadline: number) => void;
  startOnlineIntermission: (round: number, recipe: Recipe, winner: RoundWinner, score: [number, number], endsAt: number) => void;
  acceptOnlineMove: (grid: Grid, inventory: InventorySlot[]) => void;
  applyFreeze: (freezeEndsAt: number, deadline?: number) => void;
  applyScramble: (order: number[]) => void;
  updateOnlinePlayer: (self: boolean, grid: Grid) => void;
  finishOnlineRound: (winner: RoundWinner, score: [number, number], matchOver: boolean) => void;
  toggleSound: () => void;
};

const emptyGrid = (): Grid => Array.from({ length: 9 }, () => null);

function randomSeed(): number {
  if (globalThis.crypto) {
    const values = new Uint32Array(1);
    globalThis.crypto.getRandomValues(values);
    return values[0]!;
  }
  return Math.floor(Math.random() * 0x1_0000_0000);
}

function roundSetup(round: number, matchSeed: number) {
  const recipe = selectRoundValueByFamily(
    MATCH_RECIPES,
    matchSeed,
    round,
    (candidate) => getMatchItemFamily(candidate.outputItemId),
    (candidate) => String(candidate.grid.filter((item) => item !== null).length)
  );
  const seed = hashSeed(`offline-match:${matchSeed}:${round}`);
  return {
    recipe,
    inventory: generateInventory(recipe, DECOY_ITEMS, seed, {
      itemFamily: getMatchItemFamily,
      variantSeed: matchSeed,
      round
    })
  };
}

function createAiPlacementSchedule(
  recipe: Recipe,
  difficulty: AiDifficulty,
  matchSeed: number,
  round: number,
  startsAt: number,
  roundDurationMs: number
): AiPlacement[] {
  const targetGrid = normalizeRecipeGrid(recipe);
  const ingredientSlots = targetGrid
    .map((itemId, slot) => ({ itemId, slot }))
    .filter((placement): placement is { itemId: ItemId; slot: number } => placement.itemId !== null);
  if (ingredientSlots.length === 0) return [];

  const profile = AI_DIFFICULTY_PROFILES[difficulty];
  const random = createSeededRandom(hashSeed(`ai:${matchSeed}:${round}:${difficulty}:${recipe.id}`));
  const durationScale = (0.55 + ingredientSlots.length * 0.075) * (1 + (recipe.difficulty - 3) * 0.04);
  const sampledDuration = profile.minDurationMs + random() * (profile.maxDurationMs - profile.minDurationMs);
  const totalDuration = Math.min(roundDurationMs - 1_000, Math.max(1_000, Math.round(sampledDuration * durationScale)));
  const placements = shuffleSeeded(ingredientSlots, Math.floor(random() * 0x1_0000_0000));
  const weights = placements.map((_, index) => {
    let weight = profile.jitterMin + random() * (profile.jitterMax - profile.jitterMin);
    if (index === 0) weight *= 1.25 + random() * 0.35;
    if (index > 0 && random() < profile.hesitationChance) weight *= 1.6 + random() * 0.6;
    return weight;
  });
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  let elapsedWeight = 0;

  return placements.map((placement, index) => {
    elapsedWeight += weights[index]!;
    return {
      ...placement,
      at: startsAt + Math.round((elapsedWeight / totalWeight) * totalDuration)
    };
  });
}

const initialMatchSeed = randomSeed();
const first = roundSetup(1, initialMatchSeed);

export const useGameStore = create<GameState>((set, get) => ({
  status: "ready",
  round: 1,
  score: [0, 0],
  recipe: first.recipe,
  inventory: first.inventory,
  grid: emptyGrid(),
  rivalGrid: emptyGrid(),
  selectedItem: null,
  draggingItem: null,
  deadline: null,
  durationMs: 30_000,
  aiDifficulty: "normal",
  rivalPlacementSchedule: [],
  rivalFinishAt: null,
  frozenUntil: null,
  inventoryScrambleRevision: 0,
  winner: null,
  soundEnabled: true,
  matchStartedAt: null,
  matchSeed: initialMatchSeed,
  playKind: "ai",
  onlineSend: null,

  startRound: () => {
    if (get().playKind !== "ai") return;
    const now = Date.now();
    set({
      status: "countdown",
      deadline: now + MATCH_START_SEQUENCE_MS,
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      selectedItem: null,
      draggingItem: null,
      winner: null,
      rivalGrid: emptyGrid(),
      grid: emptyGrid()
    });
  },

  selectItem: (itemId) => {
    if (get().status !== "active" || (get().frozenUntil ?? 0) > Date.now()) {
      clientWarn("item_selection_ignored", { itemId, status: get().status });
      return;
    }
    set({ selectedItem: itemId });
    clientLog("item_selected", { itemId });
  },

  beginDrag: (itemId) => {
    if (get().status !== "active" || (get().frozenUntil ?? 0) > Date.now()) {
      clientWarn("item_drag_ignored", { itemId, status: get().status });
      return;
    }
    set({ draggingItem: itemId, selectedItem: itemId });
    clientLog("item_drag_started", { itemId });
  },

  endDrag: () => {
    const itemId = get().draggingItem;
    set({ draggingItem: null });
    if (itemId) clientLog("item_drag_ended", { itemId });
  },

  placeSelected: (slot, explicitItem) => {
    const state = get();
    if (state.status !== "active" || (state.frozenUntil ?? 0) > Date.now()) {
      clientWarn("item_placement_ignored", { gridSlot: slot, reason: "round_not_active", status: state.status });
      return;
    }
    const itemId = explicitItem ?? state.draggingItem ?? state.selectedItem;
    if (!itemId) {
      clientWarn("item_placement_ignored", { gridSlot: slot, reason: "no_item_selected" });
      return;
    }
    const remaining = availableQuantity(state.inventory, state.grid, itemId);
    if (remaining <= 0) {
      clientWarn("item_placement_ignored", { gridSlot: slot, itemId, reason: "item_unavailable", remaining });
      return;
    }
    if (state.playKind === "online") {
      const displayedSlot = state.inventory.findIndex((candidate) => candidate.itemId === itemId);
      const canonicalSlot = displayedSlot < 0
        ? -1
        : Number(/^inventory-(\d+)$/.exec(state.inventory[displayedSlot]!.slotId)?.[1] ?? displayedSlot);
      const fromSlot = Number.isInteger(canonicalSlot) && canonicalSlot >= 0 && canonicalSlot <= 9
        ? canonicalSlot
        : displayedSlot;
      if (fromSlot < 0 || !state.onlineSend) {
        clientWarn("item_placement_ignored", { gridSlot: slot, itemId, inventorySlot: fromSlot, reason: fromSlot < 0 ? "inventory_slot_missing" : "socket_sender_missing" });
        return;
      }
      const next = [...state.grid];
      next[slot] = itemId;
      set({ grid: next });
      state.onlineSend({ type: "MOVE_ITEM", fromSlot, toSlot: slot });
      clientLog("item_placement_sent", { gridSlot: slot, itemId, inventorySlot: fromSlot, remaining });
      return;
    }
    const next = [...state.grid];
    next[slot] = itemId;
    if (recipeMatches(next, state.recipe)) {
      if (state.playKind === "learn") {
        set({
          grid: next,
          selectedItem: null,
          draggingItem: null,
          winner: "player",
          deadline: null,
          status: "round-result"
        });
        clientLog("learn_recipe_completed", { gridSlot: slot, itemId, recipeId: state.recipe.id, position: state.round });
        return;
      }
      const score: [number, number] = [state.score[0] + 1, state.score[1]];
      set({
        grid: next,
        selectedItem: null,
        draggingItem: null,
        score,
        winner: "player",
        deadline: Date.now() + 5_000,
        status: score[0] >= ROUNDS_TO_WIN ? "match-result" : "round-result"
      });
      clientLog("offline_recipe_completed", { gridSlot: slot, itemId, round: state.round, score });
      return;
    }
    set({ grid: next });
    clientLog("offline_item_placed", { gridSlot: slot, itemId, remainingAfter: remaining - 1 });
  },

  clearSlot: (slot) => {
    const state = get();
    if (state.status !== "active" || (state.frozenUntil ?? 0) > Date.now()) {
      clientWarn("item_removal_ignored", { gridSlot: slot, reason: "round_not_active", status: state.status });
      return;
    }
    if (state.playKind === "online") {
      if (!state.onlineSend) {
        clientWarn("item_removal_ignored", { gridSlot: slot, reason: "socket_sender_missing" });
        return;
      }
      const next = [...state.grid];
      next[slot] = null;
      set({ grid: next });
      state.onlineSend({ type: "REMOVE_ITEM", slot });
      clientLog("item_removal_sent", { gridSlot: slot, itemId: state.grid[slot] });
      return;
    }
    const next = [...state.grid];
    const itemId = next[slot];
    next[slot] = null;
    set({ grid: next });
    clientLog("offline_item_removed", { gridSlot: slot, itemId });
  },

  clearAllOfItem: (itemId) => {
    const state = get();
    if (state.status !== "active" || (state.frozenUntil ?? 0) > Date.now()) {
      clientWarn("item_group_removal_ignored", { itemId, reason: "round_not_active", status: state.status });
      return;
    }
    const matchingSlots = state.grid
      .map((candidate, slot) => candidate === itemId ? slot : -1)
      .filter((slot) => slot >= 0);
    if (matchingSlots.length === 0) return;
    if (state.playKind === "online") {
      if (!state.onlineSend) {
        clientWarn("item_group_removal_ignored", { itemId, reason: "socket_sender_missing" });
        return;
      }
      set({ grid: state.grid.map((candidate) => candidate === itemId ? null : candidate) });
      matchingSlots.forEach((slot) => state.onlineSend?.({ type: "REMOVE_ITEM", slot }));
      clientLog("item_group_removal_sent", { itemId, gridSlots: matchingSlots, quantity: matchingSlots.length });
      return;
    }
    set({ grid: state.grid.map((candidate) => candidate === itemId ? null : candidate) });
    clientLog("offline_item_group_removed", { itemId, gridSlots: matchingSlots, quantity: matchingSlots.length });
  },

  tick: (now) => {
    const state = get();
    if (state.playKind !== "ai") return;
    if (!state.deadline) return;
    if ((state.frozenUntil ?? 0) > now) return;
    if (state.matchStartedAt !== null && now >= state.matchStartedAt + 10 * 60_000) {
      set({ status: "match-result", winner: "draw", deadline: null });
      return;
    }
    if (state.status === "round-result" && now >= state.deadline) {
      get().nextRound();
      return;
    }
    if (state.status === "countdown" && now >= state.deadline) {
      const rivalPlacementSchedule = createAiPlacementSchedule(
        state.recipe,
        state.aiDifficulty,
        state.matchSeed,
        state.round,
        now,
        state.durationMs
      );
      set({
        status: "active",
        deadline: now + state.durationMs,
        rivalPlacementSchedule,
        rivalFinishAt: rivalPlacementSchedule.at(-1)?.at ?? null,
        matchStartedAt: state.matchStartedAt ?? now
      });
      return;
    }
    if (state.status !== "active") return;
    const rivalGrid = emptyGrid();
    const completedPlacements = state.rivalPlacementSchedule.filter((placement) => placement.at <= now);
    completedPlacements.forEach(({ itemId, slot }) => { rivalGrid[slot] = itemId; });

    const rivalCompleted = state.rivalPlacementSchedule.length > 0
      && completedPlacements.length === state.rivalPlacementSchedule.length;
    if (rivalCompleted || now >= state.deadline) {
      const score: [number, number] = rivalCompleted ? [state.score[0], state.score[1] + 1] : state.score;
      set({
        rivalGrid,
        score,
        winner: rivalCompleted ? "rival" : "draw",
        deadline: now + 5_000,
        status: score[0] >= ROUNDS_TO_WIN || score[1] >= ROUNDS_TO_WIN ? "match-result" : "round-result"
      });
      return;
    }
    if (rivalGrid.some((item, index) => item !== state.rivalGrid[index])) set({ rivalGrid });
  },

  nextRound: () => {
    if (get().playKind !== "ai") return;
    const round = get().round + 1;
    const setup = roundSetup(round, get().matchSeed);
    set({
      status: "countdown",
      round,
      recipe: setup.recipe,
      inventory: setup.inventory,
      grid: emptyGrid(),
      rivalGrid: emptyGrid(),
      selectedItem: null,
      draggingItem: null,
      deadline: Date.now() + ROUND_START_SEQUENCE_MS,
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      winner: null
    });
  },

  resetMatch: () => {
    const matchSeed = randomSeed();
    const setup = roundSetup(1, matchSeed);
    set({
      status: "ready",
      round: 1,
      score: [0, 0],
      recipe: setup.recipe,
      inventory: setup.inventory,
      grid: emptyGrid(),
      rivalGrid: emptyGrid(),
      selectedItem: null,
      draggingItem: null,
      deadline: null,
      aiDifficulty: "normal",
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      winner: null,
      matchStartedAt: null,
      matchSeed,
      playKind: "ai",
      onlineSend: null
    });
  },

  configureAiMatch: (difficulty) => {
    const matchSeed = randomSeed();
    const setup = roundSetup(1, matchSeed);
    set({
      status: "ready",
      round: 1,
      score: [0, 0],
      recipe: setup.recipe,
      inventory: setup.inventory,
      grid: emptyGrid(),
      rivalGrid: emptyGrid(),
      selectedItem: null,
      draggingItem: null,
      deadline: null,
      aiDifficulty: difficulty,
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      winner: null,
      matchStartedAt: null,
      matchSeed,
      playKind: "ai",
      onlineSend: null
    });
  },

  configureLearnRecipe: (recipe, sessionSeed, position) => {
    const round = Math.max(1, Math.floor(position));
    const inventorySeed = hashSeed(`learn-practice:${sessionSeed}:${round}:${recipe.id}`);
    set({
      status: "active",
      round,
      score: [0, 0],
      recipe,
      inventory: generateInventory(recipe, DECOY_ITEMS, inventorySeed, {
        itemFamily: getMatchItemFamily,
        variantSeed: sessionSeed,
        round
      }),
      grid: emptyGrid(),
      rivalGrid: emptyGrid(),
      selectedItem: null,
      draggingItem: null,
      deadline: null,
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      winner: null,
      matchStartedAt: null,
      matchSeed: sessionSeed,
      playKind: "learn",
      onlineSend: null
    });
  },

  configureOnlineMatch: (send) => {
    const matchSeed = randomSeed();
    const setup = roundSetup(1, matchSeed);
    set({
      status: "ready",
      round: 0,
      score: [0, 0],
      recipe: setup.recipe,
      inventory: [],
      grid: emptyGrid(),
      rivalGrid: emptyGrid(),
      selectedItem: null,
      draggingItem: null,
      deadline: null,
      rivalPlacementSchedule: [],
      rivalFinishAt: null,
      frozenUntil: null,
      inventoryScrambleRevision: 0,
      winner: null,
      matchStartedAt: Date.now(),
      matchSeed,
      playKind: "online",
      onlineSend: send
    });
  },

  startOnlineCountdown: (round, recipe, inventory, startsAt) => set({
    status: "countdown",
    round,
    recipe,
    inventory,
    grid: emptyGrid(),
    rivalGrid: emptyGrid(),
    selectedItem: null,
    draggingItem: null,
    deadline: startsAt,
    rivalPlacementSchedule: [],
    rivalFinishAt: null,
    frozenUntil: null,
    inventoryScrambleRevision: 0,
    winner: null
  }),

  startOnlineRound: (round, recipe, inventory, deadline) => set({ status: "active", round, recipe, inventory, deadline, frozenUntil: null }),

  startOnlineIntermission: (round, recipe, winner, score, endsAt) => set({
    status: "round-result",
    round,
    recipe,
    winner,
    score,
    deadline: endsAt,
    frozenUntil: null,
    selectedItem: null,
    draggingItem: null
  }),

  acceptOnlineMove: (grid, inventory) => {
    set({ grid, inventory });
    clientLog("online_move_accepted", { occupiedGridSlots: grid.filter(Boolean).length, inventorySlots: inventory.length });
  },

  applyFreeze: (freezeEndsAt, authoritativeDeadline) => {
    const state = get();
    const extension = Math.max(0, freezeEndsAt - Date.now());
    set({
      frozenUntil: freezeEndsAt,
      deadline: authoritativeDeadline ?? (state.deadline === null ? null : state.deadline + extension),
      rivalPlacementSchedule: state.playKind === "ai"
        ? state.rivalPlacementSchedule.map((placement) => placement.at >= freezeEndsAt - extension
          ? { ...placement, at: placement.at + extension }
          : placement)
        : state.rivalPlacementSchedule,
      rivalFinishAt: state.playKind === "ai" && state.rivalFinishAt !== null
        ? state.rivalFinishAt + extension
        : state.rivalFinishAt,
      selectedItem: null,
      draggingItem: null
    });
  },

  applyScramble: (order) => {
    const state = get();
    if (order.length !== state.inventory.length || new Set(order).size !== order.length) {
      clientWarn("inventory_scramble_ignored", { inventorySlots: state.inventory.length, order });
      return;
    }
    const canonical = new Map<number, InventorySlot>();
    state.inventory.forEach((slot, displayedIndex) => {
      const parsed = Number(/^inventory-(\d+)$/.exec(slot.slotId)?.[1] ?? displayedIndex);
      canonical.set(parsed, slot);
    });
    const inventory = order.map((index) => canonical.get(index));
    if (inventory.some((slot) => !slot)) {
      clientWarn("inventory_scramble_ignored", { reason: "invalid_slot_reference", order });
      return;
    }
    set({
      inventory: inventory as InventorySlot[],
      inventoryScrambleRevision: state.inventoryScrambleRevision + 1,
      selectedItem: null,
      draggingItem: null
    });
    clientLog("inventory_scrambled", { order });
  },

  updateOnlinePlayer: (self, grid) => set(self ? { grid } : { rivalGrid: grid }),

  finishOnlineRound: (winner, score, matchOver) => set({
    winner,
    score,
    deadline: null,
    frozenUntil: null,
    status: matchOver ? "match-result" : "round-result"
  }),

  toggleSound: () => set({ soundEnabled: !get().soundEnabled })
}));
